import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import Button from "components/common/Button";
import Input from "components/common/Input";
import TextArea from "components/common/TextAreawithLabel";
import { useAppDispatch, useAppSelector } from "hooks/store";
import { useEffect, useMemo, useState } from "react";
import { placeOrder, updateCheckoutData } from "store/slices/main.slice";
import { ORDER } from "utils/constants";

type Props = {
  setStage: (v: string) => void;
  setShowPopup: (v: boolean) => void;
  setOrderTotal: (v: number) => void;
};

const CheckoutView = ({ setStage, setShowPopup, setOrderTotal }: Props) => {
  const [tip, setTip] = useState(0);
  const [notes, setnotes] = useState("");
  const dispatch = useAppDispatch();
  const checkoutData = useAppSelector(({ main }) => main.checkoutData);
  const { settings, companyData } = useAppSelector(({ main }) => main);

  useEffect(() => {
    if (checkoutData?.tip && !tip) {
      setTip(checkoutData.tip);
    }
  }, [checkoutData, tip]);

  const updateTip = (v: number) => {
    setTip(v);
    dispatch(updateCheckoutData({ tip: v }));
  };

  const orderType = useMemo(
    () => checkoutData?.orderType,
    [checkoutData?.orderType]
  );

  const allowPayAtCounter = useMemo(() => {
    if (orderType === ORDER.TAKE_OUT) {
      return !settings.DontPayAtStorePickup;
    }
    if (orderType === ORDER.DELIVERY) {
      return !settings.DontPayAtStoreDelivery;
    }

    return true;
  }, [settings, orderType]);

  const allowOnlinePayment = useMemo(() => {
    if (orderType === ORDER.TAKE_OUT) {
      return !!settings.PickupOnlinePayment;
    }
    if (orderType === ORDER.DELIVERY) {
      return !!settings.DeliveryOnlineOrder;
    }

    return true;
  }, [settings, orderType]);

  const specialDiscount = useMemo(() => {
    if (
      !(
        (settings.AllowDiscountOnDelivery && orderType === ORDER.DELIVERY) ||
        (settings.AllowDiscountOnPickup && orderType === ORDER.TAKE_OUT)
      )
    )
      return 0;

    return +(
      (checkoutData.total * +settings.OnlineSpecialDiscount) / 100
    ).toFixed(2);
  }, [settings, orderType, checkoutData]);

  const deliveryCharge = useMemo(() => {
    if (orderType !== ORDER.DELIVERY) return 0;

    return companyData?.deliveryCharge || 0;
  }, [orderType, companyData?.deliveryCharge]);

  const handlePlaceOrder = () => {
    dispatch(
      placeOrder({
        tip,
        deliveryCharge,
        extra: deliveryCharge,
        paymentType: "oncounter",
        setStage,
        notes: notes,
        setShowPopup,
        setOrderTotal,
      }) as unknown as any
    );
  };

  // === ADDED BY SAMPATH – 2026-01-15 ===
  // Tip preset amounts + active state helpers (keeps UI consistent and allows non-theme styling)
  const tip10 = useMemo(
    () => +((checkoutData?.total * 10) / 100).toFixed(2),
    [checkoutData?.total]
  );
  const tip15 = useMemo(
    () => +((checkoutData?.total * 15) / 100).toFixed(2),
    [checkoutData?.total]
  );
  const tip20 = useMemo(
    () => +((checkoutData?.total * 20) / 100).toFixed(2),
    [checkoutData?.total]
  );
  const isTipActive = (v: number) => Math.abs((tip || 0) - v) < 0.01;
  // === END SAMPATH ===

  if (!checkoutData) return <></>;
  return (
    <>
      {/* === ADDED BY SAMPATH – 2026-01-06 === */}
      {/* Premium, single-border, mobile-first styling (scoped) */}
      <style>
        {`
          .checkoutView {
            background: #fafbfc;
          }
          .checkoutHeader {
            background: #ffffff;
          }
          .checkoutCard {
            background: #ffffff;
            border: 1px solid rgba(15, 23, 42, 0.08);
            border-radius: 14px;
            box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06);
          }
          .checkoutRow {
            padding: 8px 0;
            border-bottom: 1px dashed rgba(15, 23, 42, 0.12);
          }
          .checkoutRow:last-child {
            border-bottom: none;
          }
          .checkoutMuted {
            color: rgba(15, 23, 42, 0.70);
          }

          /* === ADDED BY SAMPATH – 2026-01-06 === */
          /* Premium Total Bar — remove theme-tinted sharp block & borders */
          .checkoutTotalBar {
            background: #ffffff !important;
            border: none !important;
            border-radius: 14px !important;
            box-shadow:
              0 10px 24px rgba(0,0,0,0.06),
              0 2px 6px rgba(0,0,0,0.04) !important;
            padding: 14px 16px !important;
          }
          .checkoutTotalBar p:first-child {
            color: #0f172a !important;
            font-weight: 600;
            letter-spacing: 0.2px;
          }
          .checkoutTotalBar p:last-child {
            color: var(--primary) !important;
            font-size: 16px;
            font-weight: 700;
          }
          /* === END SAMPATH === */

          .checkoutPillBtn {
            border-radius: 999px !important;
            padding: 8px 14px !important;
            min-height: 36px !important;
            box-shadow: none !important;
          }

          /* === ADDED BY SAMPATH – 2026-01-15 === */
          /* Tip preset buttons: modern neutral (NOT theme-colored), with clear active state */
          .checkoutView .tipBtn {
            background: rgba(15, 23, 42, 0.06) !important;
            color: rgba(15, 23, 42, 0.92) !important;
            border: 1px solid rgba(15, 23, 42, 0.10) !important;
            box-shadow: none !important;
          }
          .checkoutView .tipBtn:hover {
            background: rgba(15, 23, 42, 0.10) !important;
            border-color: rgba(15, 23, 42, 0.18) !important;
          }
          .checkoutView .tipBtnActive {
            background: rgba(15, 23, 42, 0.92) !important;
            color: #ffffff !important;
            border-color: rgba(15, 23, 42, 0.92) !important;
          }
          /* === END SAMPATH === */

          /* Force single smooth outline for MUI outlined inputs inside this view (no double borders) */
          .checkoutView .MuiOutlinedInput-root {
            border-radius: 12px !important;
            background: #ffffff !important;
          }
          .checkoutView .MuiOutlinedInput-notchedOutline {
            border: 1px solid rgba(15, 23, 42, 0.16) !important;
          }
          .checkoutView .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
            border-color: rgba(15, 23, 42, 0.28) !important;
          }
          .checkoutView .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
            border-color: rgba(16, 185, 129, 0.55) !important;
          }
          .checkoutView .MuiOutlinedInput-root.Mui-focused {
            box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.14) !important; /* soft focus glow */
          }
          .checkoutView input, .checkoutView textarea {
            font-size: 14px;
          }

          /* Make notes area look like a premium card without extra borders */
          .checkoutNotesWrap textarea {
            outline: none;
          }
        `}
      </style>
      {/* === END SAMPATH === */}

      {/* === ADDED BY SAMPATH – 2026-01-06 === */}
      {/* Header: cleaner, premium, consistent spacing */}
      {/* === END SAMPATH === */}
      <div className="checkoutHeader flex justify-between items-center px-4 pb-2 border-b">
        <div onClick={() => setStage("DETAILS")}>
          <ArrowBackIosNewIcon className="!text-[15px] !text-primary cursor-pointer" />
        </div>
        <h4 className="!text-lg text-primary font-semibold text-center">
          Check Out
        </h4>
        <div />
      </div>

      {/* === ADDED BY SAMPATH – 2026-01-06 === */}
      {/* Wrap content with a subtle app background and better spacing */}
      {/* === END SAMPATH === */}
      <div className="checkoutView px-4 max-h-[75vh] overflow-auto">
        <div className="pt-3">
          {/* === ADDED BY SAMPATH – 2026-01-06 === */}
          {/* Summary in a premium card */}
          {/* === END SAMPATH === */}
          <div className="checkoutCard px-3 py-3">
            {checkoutData?.finalBill?.map((i: any) => (
              <>
                {i.amt > 0 && (
                  <div className="flex px-1 justify-between checkoutRow">
                    <p className="font-semibold text-[14px] checkoutMuted">
                      {i.title}
                    </p>
                    <p className="font-semibold text-[14px] text-slate-900">
                      ${i.amt.toFixed(2)}
                    </p>
                  </div>
                )}
              </>
            ))}

            {!settings.DontDisplayTipOption && (
              <div className="flex px-1 justify-between checkoutRow">
                <p className="font-semibold text-[14px] checkoutMuted">Tips</p>
                <p className="font-semibold text-[14px] text-slate-900">
                  ${tip.toFixed(2)}
                </p>
              </div>
            )}

            {!!specialDiscount && (
              <div className="flex px-1 justify-between checkoutRow">
                <p className="font-semibold text-[14px] checkoutMuted">
                  Special Discount
                </p>
                <p className="font-semibold text-[14px] text-slate-900">
                  ${specialDiscount}
                </p>
              </div>
            )}

            {orderType === ORDER.DELIVERY && !!deliveryCharge && (
              <div className="flex px-1 justify-between checkoutRow">
                <p className="font-semibold text-[14px] checkoutMuted">
                  Delivery Charge
                </p>
                <p className="font-semibold text-[14px] text-slate-900">
                  ${deliveryCharge.toFixed(2)}
                </p>
              </div>
            )}

            {/* === ADDED BY SAMPATH – 2026-01-06 === */}
            {/* Total bar: premium floating card (no theme background, no sharp border) */}
            {/* === END SAMPATH === */}
            <div className="checkoutTotalBar flex justify-between mt-3">
              <p>Total</p>
              <p>${(checkoutData.total + tip + deliveryCharge).toFixed(2)}</p>
            </div>
          </div>

          {!!settings.DisplayLocationOnCheckout && (
            <>
              {/* === ADDED BY SAMPATH – 2026-01-06 === */}
              {/* Location in a matching card style */}
              {/* === END SAMPATH === */}
              <div className="checkoutCard mt-3 px-3 py-3">
                <p className="text-sm text-center checkoutMuted">
                  Your order will be placed on below location
                </p>
                <p className="text-base text-center text-primary font-semibold mt-1">
                  {companyData?.address1} {companyData?.address2}
                </p>
              </div>
            </>
          )}

          {!settings.DontDisplayTipOption && (
            <>
              {/* === ADDED BY SAMPATH – 2026-01-06 === */}
              {/* Tips row: mobile-first wrap + premium pill buttons + single-border input */}
              {/* === END SAMPATH === */}
              <div className="checkoutCard mt-3 px-3 py-3">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <p className="font-semibold text-[14px] text-slate-900 sm:min-w-[80px]">
                    Tips
                  </p>

                  <div className="flex flex-wrap gap-2">
                    <Button
                      // === ADDED BY SAMPATH – 2026-01-15 ===
                      // Neutral modern tip buttons (not theme-colored) + active state
                      className={`checkoutPillBtn tipBtn ${
                        isTipActive(tip10) ? "tipBtnActive" : ""
                      }`}
                      // === END SAMPATH ===
                      onClick={() => updateTip(tip10)}
                    >
                      10%
                    </Button>
                    <Button
                      // === ADDED BY SAMPATH – 2026-01-15 ===
                      className={`checkoutPillBtn tipBtn ${
                        isTipActive(tip15) ? "tipBtnActive" : ""
                      }`}
                      // === END SAMPATH ===
                      onClick={() => updateTip(tip15)}
                    >
                      15%
                    </Button>
                    <Button
                      // === ADDED BY SAMPATH – 2026-01-15 ===
                      className={`checkoutPillBtn tipBtn ${
                        isTipActive(tip20) ? "tipBtnActive" : ""
                      }`}
                      // === END SAMPATH ===
                      onClick={() => updateTip(tip20)}
                    >
                      20%
                    </Button>
                  </div>

                  <div className="flex-1 sm:min-w-[170px]">
                    <Input
                      size="small"
                      sx={{
                        width: "100%",
                        // === ADDED BY SAMPATH – 2026-01-06 ===
                        // Ensure clean single-outline input and premium spacing
                        "& .MuiInputBase-root": { height: 44 },
                        "& input": { padding: "12px 12px" },
                        // === END SAMPATH ===
                      }}
                      value={tip}
                      onValueChange={(t) => +t >= 0 && updateTip(+t)}
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* === ADDED BY SAMPATH – 2026-01-06 === */}
          {/* Notes: responsive width (full on mobile, centered, premium card) */}
          {/* === END SAMPATH === */}
          <div className="flex w-full justify-center">
            {/* === ADDED BY SAMPATH – 2026-01-15 === */}
            {/* Fix: keep notes from becoming full-wide; match screenshot with a centered max width */}
            {/* === END SAMPATH === */}
            <div className="checkoutNotesWrap w-full max-w-[520px] mt-4">
              <div className="checkoutCard px-3 py-3">
                <TextArea
                  label="Order Notes"
                  value={notes}
                  onChange={(e) => setnotes(e.target.value)}
                  placeholder="Add any special instructions..."
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* === ADDED BY SAMPATH – 2026-01-06 === */}
      {/* Cleaner divider spacing */}
      {/* === END SAMPATH === */}
      <div className="border-b w-full mt-3 mb-3 flex-1" />

      {allowPayAtCounter && (
        <Button className="mx-4" onClick={handlePlaceOrder}>
          Pay at Counter
        </Button>
      )}
      {allowOnlinePayment && (
        <Button
          className="mx-4 mt-2"
          onClick={() => {
            dispatch(
              updateCheckoutData({
                tip,
                deliveryCharge,
                total: checkoutData.total + tip + deliveryCharge,
              })
            );
            setStage("PAYMENT");
          }}
        >
          Pay Now
        </Button>
      )}
    </>
  );
};

export default CheckoutView;
